import { useMemo, useState, useEffect, useRef } from 'react';
import { Icon, Text, Box, Flex, Spacer, Button } from '@chakra-ui/react';

import SectionContainer from '~/containers/SectionContainer';
import PageContainer from '~/containers/PageContainer';
import Content from '~/containers/Content';

import FooterCartStatusBar from '~/components/FooterCartStatusBar';
import FooterReturnNotice from '~/components/FooterReturnNotice';
import FooterLoginSection from '~/components/FooterLoginSection';
import PreOrderTimeBar from '~/components/PreOrderTimeBar';
import LocationBar from '~/components/LocationBar';
import GoTopButton from '~/components/GoTopButton';
import BackwardBar from '~/components/BackwardBar';
import InfinityItemList from '~/components/InfinityItemList';

import { AiOutlineShop } from 'react-icons/ai';
import { IoChevronForward } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';

import currentPickMealPointSelector from '~/recoil/selectors/currentPickMealPoint';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import isHostSelector from '~/recoil/selectors/isHost';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import platformAtom from '~/recoil/atom/platform';
import displayStoresSelector from '~/recoil/selectors/displayStores';
import authAtom from '~/recoil/atom/auth';
import modalControlAtom from '~/recoil/atom/modalControl';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import useStoreList from '~/hooks/useStoreList';
import useCartId from '~/hooks/useCartId';
import useRestItem from './hooks/useRestItems';
import usePinnedItems from '~/pages/Home/hooks/usePinnedItems';
import usePopularItems from '~/pages/Home/hooks/usePopularItems';

import * as fp from 'lodash/fp';

import transformDateRangeToString from '~/utils/transformDateRangeToString';
import isCanPreOrderTime from '~/utils/isCanPreOrderTime';
import isInOperatingTime from '~/utils/isInOperatingTime';
import getDateInventory from '~/utils/getDateInventory';

import { FetchingDataStatus } from '~/types/utilities';
import { CartType } from '~/types/cart';
import { UniqItem } from '~/types/store/item';
import EmptySection from '~/components/EmptySection';

export default function ItemList() {
  const auth = useRecoilValue(authAtom);
  const pickMealAt = useRecoilValue(pickMealAtAtom);
  const pickMealEnd = useRecoilValue(pickMealEndAtom);
  const platform = useRecoilValue(platformAtom);
  const displayStores = useRecoilValue(displayStoresSelector);
  const currentPickMealPoint = useRecoilValue(currentPickMealPointSelector);
  const isHost = useRecoilValue(isHostSelector);
  const setModalControl = useSetRecoilState(modalControlAtom);

  const { cartType, cartId } = useCartId();

  const itemListRef = useRef<any>();

  const { groupId = '' } = useParams();
  const navigate = useNavigate();

  const [showHeader, setShowHeader] = useState<boolean>(true);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const availableStores = useMemo(
    () => currentPickMealPoint.stores.filter((storeId) => displayStores.includes(storeId)),
    [displayStores, currentPickMealPoint.stores],
  );

  const { storeList, fetchingStatus } = useStoreList(availableStores);
  const filteredStores = useMemo(() => {
    const now = new Date();
    return storeList.filter(
      ({ preOrderSetting: { schedule, afterHours, beforeHours } }) =>
        pickMealAt &&
        isCanPreOrderTime(schedule, afterHours, beforeHours, pickMealAt, now) &&
        isInOperatingTime(schedule, pickMealAt),
    );
  }, [storeList, pickMealAt]);

  const filteredStoreIds = useMemo(
    () => filteredStores.map(({ objectID }) => objectID),
    [filteredStores],
  );
  const pinnedItems = usePinnedItems(platform.pinnedItems);
  const popularItems = usePopularItems(filteredStoreIds, pickMealAt, pinnedItems);

  const {
    items,
    fetchingStatus: fetchingStatusOfItem,
    handleLoadNextPage,
  } = useRestItem(filteredStoreIds, pickMealAt);

  const composedPopularItems = useMemo(() => {
    return popularItems
      .filter(({ storeId }) => filteredStoreIds.includes(storeId))
      .filter(
        ({ isAvailable, inventory }) =>
          isAvailable && fp.gt(getDateInventory(inventory, pickMealAt), 0),
      );
  }, [popularItems, filteredStoreIds, pickMealAt]);

  const displayItems = useMemo(
    () =>
      fp.uniqBy<UniqItem>('uniqId')(
        composedPopularItems.concat(
          items.filter(
            ({ isAvailable, inventory }) =>
              isAvailable && fp.gt(getDateInventory(inventory, pickMealAt), 0),
          ),
        ),
      ),

    [items, composedPopularItems, pickMealAt],
  );

  const handleSetDisplayHeader = (val: boolean) => {
    if (val === showHeader) {
      return;
    }
    setShowHeader(val);
  };

  const handleSetItemListRef = (list: any) => {
    itemListRef.current = list;
  };

  return (
    <PageContainer
      hideNavBar
      hideHeader
      afterHeaderComponent={
        <>
          <BackwardBar
            title="返回"
            onNavigate={() => navigate(groupId ? `/group/${groupId}` : `/`)}
          />
          <LocationBar
            rightHandComponent={
              <Text fontSize="xs" color="white">
                你所選的取餐地點
              </Text>
            }
          />
          <PreOrderTimeBar
            pickMealTime={transformDateRangeToString(pickMealAt, pickMealEnd)}
            onClick={() =>
              !(fp.eq(cartType, CartType.CART_GROUP) && !isHost) &&
              setModalControl((prev) => ({ ...prev, isPickupSettingModalOPen: true }))
            }
            rightHandComponent={
              <Button
                size="sm"
                variant="unstyled"
                color="primary"
                fontSize="sm"
                rightIcon={<Icon as={IoChevronForward} />}
                letterSpacing="0.1em"
              >
                選擇取餐時間
              </Button>
            }
          />
        </>
      }
    >
      <Box bg="white">
        <Content minH={window.innerHeight - 70} py={4}>
          {showHeader && (
            <SectionContainer
              title="全部餐點"
              icon={<Icon as={AiOutlineShop} color="secondary" boxSize="1.25rem" />}
            />
          )}
          {fp.eq(fetchingStatus, FetchingDataStatus.SUCCESS) && !fp.eq(displayItems.length, 0) && (
            <InfinityItemList
              itemCount={displayItems.length + 1}
              items={displayItems}
              fetchingStatusOfItem={fetchingStatusOfItem}
              onLoadNextPage={handleLoadNextPage}
              onNavigateToItem={(storeId, itemId) =>
                navigate(
                  groupId
                    ? `/group/${groupId}/store/${storeId}/item/${itemId}/add`
                    : `/store/${storeId}/item/${itemId}/add`,
                  { state: { previous: window.location.pathname } },
                )
              }
              onSetHeaderDisplay={handleSetDisplayHeader}
              onSetItemListRef={handleSetItemListRef}
            />
          )}
          {fp.eq(fetchingStatus, FetchingDataStatus.SUCCESS) && fp.eq(displayItems.length, 0) && (
            <EmptySection content="目前無開放點餐餐點" />
          )}

          {fp.eq(fetchingStatus, FetchingDataStatus.FAIL) && (
            <EmptySection content="獲取資料發生錯誤請重試" />
          )}
        </Content>

        <Box position="sticky" bottom="0">
          <FooterReturnNotice />

          <Box px={4}>
            <Content py={2}>
              <Flex>
                <Spacer />
                <GoTopButton onClick={() => itemListRef.current?.scrollTo(0)} />
              </Flex>
            </Content>
          </Box>

          <>
            {fp.eq(cartType, CartType.CART_PERSONAL) && !auth ? (
              <FooterLoginSection />
            ) : (
              <FooterCartStatusBar
                onNavigate={() =>
                  navigate(fp.eq(cartType, CartType.CART_GROUP) ? `/group/${cartId}/cart` : '/cart')
                }
              />
            )}
          </>
        </Box>
      </Box>
    </PageContainer>
  );
}
